/**
 * .io-style ad placements for Dogdell Park.
 *
 * Priority (game canvas first):
 *   1. top - 728×90 leaderboard
 *   2. left/right - 160×600 rails (wide viewports only via CSS)
 *   3. footer - 728×90 (tall viewports only via CSS)
 *   4. gate - under the join card
 *
 * When AdSense isn't configured, only a compact top placeholder is shown
 * so empty dashed boxes don't steal the park.
 */

export type AdPlacement = "top" | "left" | "right" | "footer" | "gate";

export interface AdsConfig {
  enabled: boolean;
  clientId: string;
  slots: Partial<Record<AdPlacement, string>>;
}

export function loadAdsConfig(): AdsConfig {
  const clientId = (import.meta.env.VITE_ADSENSE_CLIENT_ID ?? "").trim();
  const enabledFlag = (import.meta.env.VITE_ADS_ENABLED ?? "").toLowerCase();
  const enabled =
    enabledFlag === "true" ||
    enabledFlag === "1" ||
    (enabledFlag !== "false" && enabledFlag !== "0" && Boolean(clientId));

  return {
    enabled: enabled && Boolean(clientId),
    clientId,
    slots: {
      top: (import.meta.env.VITE_ADS_SLOT_TOP ?? "").trim() || undefined,
      left: (import.meta.env.VITE_ADS_SLOT_LEFT ?? "").trim() || undefined,
      right: (import.meta.env.VITE_ADS_SLOT_RIGHT ?? "").trim() || undefined,
      footer: (import.meta.env.VITE_ADS_SLOT_FOOTER ?? "").trim() || undefined,
      gate: (import.meta.env.VITE_ADS_SLOT_GATE ?? "").trim() || undefined,
    },
  };
}

declare global {
  interface Window {
    adsbygoogle?: unknown[];
  }
}

let scriptPromise: Promise<void> | null = null;

function loadAdSenseScript(clientId: string): Promise<void> {
  if (scriptPromise) {
    return scriptPromise;
  }
  scriptPromise = new Promise((resolve, reject) => {
    if (document.querySelector(`script[data-ad-client="${clientId}"]`)) {
      resolve();
      return;
    }
    const script = document.createElement("script");
    script.async = true;
    script.crossOrigin = "anonymous";
    script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${encodeURIComponent(clientId)}`;
    script.dataset.adClient = clientId;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("AdSense script failed to load"));
    document.head.append(script);
  });
  return scriptPromise;
}

function mountSlot(
  el: HTMLElement,
  clientId: string,
  slotId: string,
  format: "auto" | "horizontal" | "vertical" = "auto",
): void {
  el.hidden = false;
  el.classList.add("is-live");
  const ins = document.createElement("ins");
  ins.className = "adsbygoogle";
  ins.style.display = "block";
  ins.dataset.adClient = clientId;
  ins.dataset.adSlot = slotId;
  ins.dataset.adFormat = format === "vertical" ? "vertical" : format === "horizontal" ? "horizontal" : "auto";
  if (format === "auto") {
    ins.dataset.fullWidthResponsive = "true";
  }
  const holder = el.querySelector(".ad-live") ?? el;
  if (holder.classList.contains("ad-live")) {
    holder.replaceChildren(ins);
  } else {
    el.append(ins);
  }
  try {
    (window.adsbygoogle = window.adsbygoogle || []).push({});
  } catch {
    // Ad blockers - drop the reserved chrome so the park keeps the space.
    el.hidden = true;
    el.classList.remove("is-live");
  }
}

const ROOTS: { placement: AdPlacement; selector: string; format: "auto" | "horizontal" | "vertical" }[] = [
  { placement: "top", selector: "#ad-top", format: "horizontal" },
  { placement: "left", selector: "#ad-left", format: "vertical" },
  { placement: "right", selector: "#ad-right", format: "vertical" },
  { placement: "footer", selector: "#ad-footer", format: "horizontal" },
  { placement: "gate", selector: "#ad-gate", format: "auto" },
];

/** Shows live units when configured; otherwise frees park chrome. */
export async function initAds(): Promise<void> {
  const config = loadAdsConfig();
  document.documentElement.dataset.ads = config.enabled ? "live" : "off";

  // Start hidden - only reveal slots we actually use.
  for (const { selector } of ROOTS) {
    const el = document.querySelector<HTMLElement>(selector);
    if (el) {
      el.hidden = true;
    }
  }

  if (!config.enabled) {
    // Dev / no AdSense: keep a slim top reminder only (not four empty billboards).
    const top = document.querySelector<HTMLElement>("#ad-top");
    if (top) {
      top.hidden = false;
      top.classList.add("is-dev-placeholder");
    }
    return;
  }

  try {
    await loadAdSenseScript(config.clientId);
  } catch {
    return;
  }

  for (const { placement, selector, format } of ROOTS) {
    const el = document.querySelector<HTMLElement>(selector);
    const slot = config.slots[placement];
    if (!el || !slot) {
      continue;
    }
    mountSlot(el, config.clientId, slot, format);
  }

  // If no side/footer slots were configured, collapse the grid to game-first.
  const left = document.querySelector<HTMLElement>("#ad-left");
  const right = document.querySelector<HTMLElement>("#ad-right");
  if (left?.hidden && right?.hidden) {
    document.documentElement.dataset.adsRails = "off";
  }
}
